"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.round = round;
exports.isValidCost = isValidCost;
exports.isValidNegativeCost = isValidNegativeCost;
exports.convertToValidCost = convertToValidCost;
exports.convertToCents = convertToCents;
exports.convertFromCents = convertFromCents;
exports.formatCurrency = formatCurrency;
exports.friendlyMoney = friendlyMoney;
exports.friendlyMoneyPair = friendlyMoneyPair;
exports.floatToPercent = floatToPercent;
exports.getCurrencyFromActiveContract = getCurrencyFromActiveContract;
exports.getCurrencyByCountrySlug = getCurrencyByCountrySlug;
exports.isNonNativeCurrencyOfCountry = isNonNativeCurrencyOfCountry;
exports.currencyNegativeFormatRegex = exports.currencyFormatRegex = void 0;

var _isNil = _interopRequireDefault(require("lodash/isNil"));

var _general = require("./general");

var currencyFormatRegex = new RegExp(/(?=\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)(\.\d{1,2})?$/);
exports.currencyFormatRegex = currencyFormatRegex;
var currencyNegativeFormatRegex = new RegExp(/(?=[\d,-])^\$?-?(([1-9]\d{0,2}(,\d{3})*)|\d+)(\.\d{1,2})?$/);
exports.currencyNegativeFormatRegex = currencyNegativeFormatRegex;

function round(value) {
  return Number(value.toFixed(2));
}

function isValidCost(value) {
  return currencyFormatRegex.test(value);
}

function isValidNegativeCost(value) {
  return currencyNegativeFormatRegex.test(value);
}
/**
 * Because amounts from forms will be as in US format, with commas, we need to convert them
 * to floats first to convert them to cents.
 * @param {*} value value to be converted
 */


function convertToValidCost(value) {
  return parseFloat(value.replace(/,/g, ''));
}

function convertToCents(amount) {
  if ((0, _isNil["default"])(amount) || amount === '') return null;
  var validAmount = amount;

  if (typeof amount === 'string') {
    validAmount = convertToValidCost(amount);
  }

  return round(validAmount * 100);
}

function convertFromCents(value) {
  if ((0, _isNil["default"])(value)) return null;
  var normalizedValue = value;

  if (typeof amount === 'string') {
    normalizedValue = convertToValidCost(value);
  }

  return round(normalizedValue / 100);
}

function friendlyError(message) {
  (0, _general.error)(message);
  return (0, _general.friendlyLabel)(null);
}
/**
 *
 * Formats the amount according to the currency.
 * It should not be used to directly render amounts of money, as it will
 * throw an error if the value is still undefined (because it's loading),
 * use the "friendlyMoney" function for that.
 *
 * @param {Float} value - the value (not in cents)
 * @param {String} currencyCode - currency code (will influence the symbol shown)
 */


function formatCurrency(value, currencyCode) {
  if (value === undefined) {
    return friendlyError('Missing "value" on the formatCurrency function');
  }

  if (!currencyCode) {
    return friendlyError('Missing "currencyCode" on the formatCurrency function');
  }

  if (value === null) {
    return null;
  }

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
/**
 *
 * The majority of the money values we deal with are in cents,
 * this converts it before calling the function above.
 * It should not be used to directly render amounts of money, as it will
 * throw an error if the value is still undefined (because it's loading),
 * use the "friendlyMoney" function for that.
 *
 * @param {Number} amountInCents - the value
 * @param {Object} currency - currency object which should contain a "code" property
 */


function formatMoney(amountInCents, currency) {
  if (amountInCents === undefined) {
    return friendlyError('Undefined "amountInCents" parameter on the formatMoney function.');
  }

  if (!currency || !currency.code) {
    return friendlyError('Missing "currency" object or currency code on the formatMoney function');
  }

  if (amountInCents === null) {
    return null;
  }

  return formatCurrency(convertFromCents(amountInCents), currency === null || currency === void 0 ? void 0 : currency.code);
}
/**
 *
 * Renders a user-friendly label if the value is null-ish or the formatted value
 *
 * @param {Number} amountInCents - the value
 * @param {Object} currency - currency object which should contain a "code" property
 */


function friendlyMoney(amountInCents, currency) {
  if (amountInCents === undefined || amountInCents === null) {
    return (0, _general.friendlyLabel)(amountInCents);
  }

  if (!currency) {
    return friendlyError('Missing "currency" object on the friendlyMoney function');
  }

  return (0, _general.friendlyLabel)(formatMoney(amountInCents, currency));
}
/**
 *
 * Renders a user-friendly label if the value is null-ish or
 * the formatted converted and source amounts
 *
 * @param {Object} money - contains all the converted/source info
 * @param {String} convertedAmountProperty - (optional) if the converted amount property has a diferent name
 * @param {String} convertedCurrencyProperty - (optional) if the converted currency property has a diferent name
 * @param {String} sourceAmountProperty - (optional) if the source amount property has a diferent name
 * @param {String} sourceCurrencyProperty - (optional) if the source currency property has a diferent name
 */


function friendlyMoneyPair(money) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$convertedAmountP = _ref.convertedAmountProperty,
      convertedAmountProperty = _ref$convertedAmountP === void 0 ? 'convertedAmount' : _ref$convertedAmountP,
      _ref$convertedCurrenc = _ref.convertedCurrencyProperty,
      convertedCurrencyProperty = _ref$convertedCurrenc === void 0 ? 'convertedCurrency' : _ref$convertedCurrenc,
      _ref$sourceAmountProp = _ref.sourceAmountProperty,
      sourceAmountProperty = _ref$sourceAmountProp === void 0 ? 'sourceAmount' : _ref$sourceAmountProp,
      _ref$sourceCurrencyPr = _ref.sourceCurrencyProperty,
      sourceCurrencyProperty = _ref$sourceCurrencyPr === void 0 ? 'sourceCurrency' : _ref$sourceCurrencyPr;

  var nullLabel = "".concat((0, _general.friendlyLabel)(null), " (").concat((0, _general.friendlyLabel)(null), ")");

  if (money === undefined || money === null) {
    return nullLabel;
  }

  if (money[sourceAmountProperty] === undefined) {
    return nullLabel;
  }

  if (money[sourceCurrencyProperty] === undefined) {
    return friendlyError("Property ".concat(money[sourceCurrencyProperty], " not found in ").concat(money, "."));
  }

  if (money[convertedAmountProperty] === undefined) {
    return nullLabel;
  }

  if (money[convertedCurrencyProperty] === undefined) {
    return friendlyError("Property ".concat(money[convertedCurrencyProperty], " not found in ").concat(money, "."));
  }

  var convertedMoney = (0, _general.friendlyLabel)(formatMoney(money[convertedAmountProperty], money[convertedCurrencyProperty]));
  var sourceMoney = (0, _general.friendlyLabel)(formatMoney(money[sourceAmountProperty], money[sourceCurrencyProperty]));
  return "".concat(convertedMoney, " (").concat(sourceMoney, ")");
}

function floatToPercent(floatValue) {
  return "".concat(floatValue * 100, "%");
}

function getCurrencyFromActiveContract(contracts) {
  var _activeContract$count, _activeContract$count2, _activeContract$count3;

  if (!contracts || contracts.length === 0) return null;
  var activeContract = contracts.find(function (contract) {
    return contract.active;
  });
  return (_activeContract$count = activeContract === null || activeContract === void 0 ? void 0 : (_activeContract$count2 = activeContract.country) === null || _activeContract$count2 === void 0 ? void 0 : (_activeContract$count3 = _activeContract$count2.currency) === null || _activeContract$count3 === void 0 ? void 0 : _activeContract$count3.code) !== null && _activeContract$count !== void 0 ? _activeContract$count : null;
}
/**
 * Get currency given a country slug
 * @param {String} countrySlug country slug
 */


function getCurrencyByCountrySlug(countrySlug, countries) {
  var _countries$find;

  return (_countries$find = countries.find(function (country) {
    return country.slug === countrySlug;
  })) === null || _countries$find === void 0 ? void 0 : _countries$find.currency;
}

function isNonNativeCurrencyOfCountry(currency, country, countries) {
  // In the majority of the cases, the currency is the native one
  // picking a non-native currency is the exception
  if (!currency || !country || !countries) {
    return false;
  }

  var countrySlug = country === null || country === void 0 ? void 0 : country.slug;
  var countryCurrency = countries ? getCurrencyByCountrySlug(countrySlug, countries) : null;
  return (currency === null || currency === void 0 ? void 0 : currency.slug) !== (countryCurrency === null || countryCurrency === void 0 ? void 0 : countryCurrency.slug);
}